import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { TabView, TabPanel } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { Badge } from "primereact/badge";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { Divider } from "primereact/divider";
import { ProgressBar } from "primereact/progressbar";
import { Chip } from "primereact/chip";

import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";

const ServiceVendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Vendor Modal State
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorFormData, setVendorFormData] = useState({
    name: "",
    service: "",
    phone: "",
    email: "",
  });

  // Asset Assignment Modal State
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [assetFormData, setAssetFormData] = useState({
    vendorId: null,
    agreement: "",
    dates: [],
    assignedAssets: [],
  });

  // Maintenance Details Modal State
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);

  // Statistics State
  const [vendorStats, setVendorStats] = useState({
    totalVendors: 0,
    activeVendors: 0,
    totalAssets: 0,
    assignedAssets: 0,
    upcomingMaintenance: 0,
  });

  const toast = useRef(null);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  useEffect(() => {
    if (selectedFacilityId) {
      fetchVendors();
      fetchAssets();
    }
  }, [selectedFacilityId]);

  const showToast = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail });
  };

  const calculateStats = (vendorData, assetData) => {
    const totalVendors = vendorData.length;
    const activeVendors = vendorData.filter(
      (vendor) => vendor.assignedAssets && vendor.assignedAssets.length > 0,
    ).length;
    const assignedAssets = vendorData.reduce(
      (sum, vendor) =>
        sum + (vendor.assignedAssets ? vendor.assignedAssets.length : 0),
      0,
    );
    const upcomingMaintenance = vendorData.reduce((sum, vendor) => {
      if (vendor.dates) {
        return (
          sum +
          vendor.dates.filter((date) => new Date(date) >= new Date()).length
        );
      }
      return sum;
    }, 0);

    setVendorStats({
      totalVendors,
      activeVendors,
      totalAssets: assetData.length,
      assignedAssets,
      upcomingMaintenance,
    });
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/service_vendor/${selectedFacilityId}`,
        "GET",
      );

      if (response.success) {
        const vendorData = response.data?.data || response.data || [];
        setVendors(vendorData);
        calculateStats(vendorData, assets);
      } else {
        setVendors([]);
        showToast("error", "Error", "Failed to fetch service vendors");
      }
    } catch (error) {
      setVendors([]);
      showToast("error", "Error", "Failed to fetch service vendors");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssets = async (vendorToEdit = null) => {
    try {
      const response = await makeRequest2(
        `/api/assets/${selectedFacilityId}`,
        "GET",
      );

      if (response.success && response.data) {
        let normalizedAssets = [];
        const data = response.data;

        if (Array.isArray(data)) {
          normalizedAssets = data;
        } else if (data.assets && Array.isArray(data.assets)) {
          normalizedAssets = data.assets;
        } else if (data.assets) {
          normalizedAssets = [data.assets];
        } else if (data._id) {
          normalizedAssets = [data];
        }

        // Get IDs of assets currently assigned to the vendor being edited
        const vendorAssignedAssetIds = vendorToEdit
          ? (vendorToEdit.assignedAssets || []).map(
              (asset) => asset.value || asset._id,
            )
          : [];

        // Format assets for MultiSelect
        const formattedAssets = normalizedAssets
          .filter(
            (asset) =>
              asset &&
              asset._id &&
              asset.name &&
              (!asset.assignedVendorId ||
                (vendorToEdit && vendorAssignedAssetIds.includes(asset._id))),
          )
          .map((asset) => ({
            label: asset.name,
            value: asset._id,
          }));

        setAssets(formattedAssets);
        calculateStats(vendors, normalizedAssets);
      } else {
        setAssets([]);
        showToast("error", "Error", "Failed to fetch facility assets");
      }
    } catch (error) {
      console.error("Asset fetch error:", error);
      showToast("error", "Error", "Failed to fetch facility assets");
      setAssets([]);
    }
  };

  const filteredVendors = vendors.filter((vendor) =>
    Object.values(vendor).some((value) =>
      value?.toString().toLowerCase().includes(searchKeyword.toLowerCase()),
    ),
  );

  const maintenanceVendors = filteredVendors.filter(
    (vendor) => vendor.assignedAssets && vendor.assignedAssets.length > 0,
  );

  // Form handlers
  const handleVendorInputChange = (e) => {
    setVendorFormData({
      ...vendorFormData,
      [e.target.name]: e.target.value,
    });
  };

  const validateVendorForm = () => {
    if (!vendorFormData.name.trim()) {
      showToast("error", "Validation Error", "Vendor name is required");
      return false;
    }
    if (!vendorFormData.service.trim()) {
      showToast("error", "Validation Error", "Service is required");
      return false;
    }
    const phoneRegex = /^(?:\+254|0)\d{9}$/;
    if (!phoneRegex.test(vendorFormData.phone)) {
      showToast(
        "error",
        "Validation Error",
        "Phone number must start with +254 or 0 followed by 9 digits",
      );
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(vendorFormData.email)) {
      showToast(
        "error",
        "Validation Error",
        "Please provide a valid email address",
      );
      return false;
    }
    return true;
  };

  const handleVendorModalOpen = (vendor = null) => {
    if (vendor) {
      setSelectedVendor(vendor);
      setVendorFormData({
        name: vendor.name || "",
        service: vendor.service || "",
        phone: vendor.phone || "",
        email: vendor.email || "",
      });
    } else {
      setSelectedVendor(null);
      setVendorFormData({
        name: "",
        service: "",
        phone: "",
        email: "",
      });
    }
    setShowVendorModal(true);
  };

  const handleAssetModalOpen = async (vendor) => {
    setSelectedVendor(vendor);
    await fetchAssets(vendor);
    setAssetFormData({
      vendorId: vendor._id,
      agreement: vendor.agreement || "",
      dates: vendor.dates ? vendor.dates.map((date) => new Date(date)) : [],
      assignedAssets: vendor.assignedAssets
        ? vendor.assignedAssets.map((asset) => asset.value || asset._id)
        : [],
    });
    setShowAssetModal(true);
  };

  const handleMaintenanceModalOpen = (vendor) => {
    setSelectedVendor(vendor);
    setShowMaintenanceModal(true);
  };

  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    if (!validateVendorForm()) return;

    try {
      setLoading(true);
      const method = selectedVendor ? "PUT" : "POST";
      const url = selectedVendor
        ? `/api/service_vendor/${selectedFacilityId}/${selectedVendor._id}`
        : `/api/service_vendor/${selectedFacilityId}`;

      const response = await makeRequest2(url, method, vendorFormData);

      if (response.success) {
        showToast(
          "success",
          "Success",
          `Service Vendor ${selectedVendor ? "updated" : "added"} successfully`,
        );
        await fetchVendors();
        setShowVendorModal(false);
      } else {
        showToast(
          "error",
          "Error",
          response.error || "Failed to process vendor",
        );
      }
    } catch (error) {
      showToast(
        "error",
        "Error",
        error.message ||
          `Failed to ${selectedVendor ? "update" : "add"} Service Vendor`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAssetSubmit = async (e) => {
    e.preventDefault();
    if (!assetFormData.agreement.trim()) {
      showToast("error", "Validation Error", "Agreement is required");
      return;
    }
    if (assetFormData.assignedAssets.length === 0) {
      showToast(
        "error",
        "Validation Error",
        "At least one asset must be assigned",
      );
      return;
    }

    try {
      setLoading(true);
      const submissionData = {
        ...assetFormData,
        dates: assetFormData.dates.map((date) =>
          date instanceof Date
            ? date.toISOString()
            : new Date(date).toISOString(),
        ),
      };

      const response = await makeRequest2(
        `/api/service_vendor/${selectedFacilityId}/${assetFormData.vendorId}`,
        "PUT",
        submissionData,
      );

      if (response.success) {
        showToast(
          "success",
          "Success",
          "Vendor assets and details updated successfully",
        );
        await Promise.all([fetchVendors(), fetchAssets()]);
        setShowAssetModal(false);
      } else {
        showToast(
          "error",
          "Error",
          response.error || "Failed to update vendor",
        );
      }
    } catch (error) {
      showToast("error", "Error", error.message || "Failed to update vendor");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVendor = (vendor) => {
    confirmDialog({
      message: `Are you sure you want to delete vendor "${vendor.name}"?`,
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const response = await makeRequest2(
            `/api/service_vendor/${selectedFacilityId}/${vendor._id}`,
            "DELETE",
          );
          if (response.success) {
            showToast(
              "success",
              "Success",
              "Service Vendor deleted successfully",
            );
            await Promise.all([fetchVendors(), fetchAssets()]);
          } else {
            showToast(
              "error",
              "Error",
              response.error || "Failed to delete vendor",
            );
          }
        } catch (error) {
          showToast("error", "Error", "Failed to delete Service Vendor");
        }
      },
    });
  };

  // Column templates
  const contactTemplate = (rowData) => {
    return (
      <div>
        <div className="d-flex align-items-center mb-1">
          <i className="pi pi-phone me-2 text-muted"></i>
          <small>{rowData.phone}</small>
        </div>
        <div className="d-flex align-items-center">
          <i className="pi pi-envelope me-2 text-muted"></i>
          <small>{rowData.email}</small>
        </div>
      </div>
    );
  };

  const assignedAssetsTemplate = (rowData) => {
    const assetCount = rowData.assignedAssets
      ? rowData.assignedAssets.length
      : 0;
    return (
      <div className="d-flex align-items-center gap-2">
        <Badge
          value={assetCount}
          severity={assetCount > 0 ? "success" : "secondary"}
        />
        <small className="text-muted">
          {assetCount} asset{assetCount !== 1 ? "s" : ""}
        </small>
      </div>
    );
  };

  const maintenanceDateTemplate = (rowData) => {
    if (!rowData.dates || rowData.dates.length === 0) {
      return (
        <Tag value="No Dates" severity="contrast" className="font-semibold" />
      );
    }

    const upcomingDates = rowData.dates
      .filter((date) => new Date(date) >= new Date())
      .sort((a, b) => new Date(a) - new Date(b));

    if (upcomingDates.length === 0) {
      return (
        <Tag value="No Upcoming" severity="warning" className="font-semibold" />
      );
    }

    const nextDate = new Date(upcomingDates[0]);
    const daysUntil = Math.ceil(
      (nextDate - new Date()) / (1000 * 60 * 60 * 24),
    );

    return (
      <div>
        <div className="text-muted small">{nextDate.toLocaleDateString()}</div>
        <Tag
          value={`${daysUntil} days`}
          severity={
            daysUntil <= 7 ? "danger" : daysUntil <= 30 ? "warning" : "success"
          }
          className="font-semibold"
        />
      </div>
    );
  };

  const vendorActionTemplate = (rowData) => {
    return (
      <div className="d-flex gap-1 flex-wrap">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined p-button-info"
          size="small"
          onClick={() => handleVendorModalOpen(rowData)}
          tooltip="Edit Vendor"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-cog"
          className="p-button-rounded p-button-outlined p-button-success"
          size="small"
          onClick={() => handleAssetModalOpen(rowData)}
          tooltip="Assign Assets"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-outlined p-button-danger"
          size="small"
          onClick={() => handleDeleteVendor(rowData)}
          tooltip="Delete Vendor"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const maintenanceActionTemplate = (rowData) => {
    return (
      <Button
        icon="pi pi-eye"
        label="View Details"
        className="p-button-info p-button-sm"
        onClick={() => handleMaintenanceModalOpen(rowData)}
      />
    );
  };

  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
        <div className="d-flex align-items-center gap-3">
          <i className="pi pi-search text-muted"></i>
          <InputText
            type="text"
            placeholder="Search vendors..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="p-inputtext-sm"
            style={{ width: "300px" }}
          />
        </div>
        <Button
          label="Add New Vendor"
          icon="pi pi-plus"
          onClick={() => handleVendorModalOpen()}
          className="p-button-primary"
        />
      </div>
    );
  };

  // Statistics cards component
  const StatisticsCards = () => {
    if (loading) {
      return (
        <div className="row mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="col-md-3 col-sm-6 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  <Skeleton height="60px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="row mb-4">
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-primary text-white h-100 shadow-sm statistics-card">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">{vendorStats.totalVendors}</h2>
                <p className="mb-0 text-white-50">Total Vendors</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-users"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-success text-white h-100 shadow-sm statistics-card">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">{vendorStats.activeVendors}</h2>
                <p className="mb-0 text-white-50">Active Vendors</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-check-circle"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-info text-white h-100 shadow-sm statistics-card">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">
                  {vendorStats.assignedAssets}
                </h2>
                <p className="mb-0 text-white-50">Assigned Assets</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-sitemap"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-warning text-white h-100 shadow-sm statistics-card">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">
                  {vendorStats.upcomingMaintenance}
                </h2>
                <p className="mb-0 text-white-50">Upcoming Services</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-calendar"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <style jsx>{`
        .p-datatable .p-datatable-tbody > tr > td {
          padding: 0.75rem;
          border-color: #e9ecef;
        }

        .p-datatable .p-datatable-thead > tr > th {
          padding: 1rem 0.75rem;
          background: #f8f9fa;
          border-color: #dee2e6;
          font-weight: 600;
          color: #495057;
        }

        .p-tabview .p-tabview-nav li .p-tabview-nav-link {
          padding: 1rem 1.5rem;
          font-weight: 500;
        }

        .statistics-card {
          transition: transform 0.2s ease-in-out;
        }

        .statistics-card:hover {
          transform: translateY(-2px);
        }

        .p-dialog .p-dialog-header {
          background: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }

        .p-dialog .p-dialog-content {
          padding: 2rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          font-weight: 500;
          color: #495057;
          margin-bottom: 0.5rem;
          display: block;
        }

        .p-card {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border-radius: 8px;
        }

        .p-card .p-card-body {
          padding: 1.5rem;
        }

        .vendor-info-alert {
          background: #e3f2fd;
          border: 1px solid #bbdefb;
          border-radius: 6px;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .maintenance-details {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1rem;
        }

        .date-chip {
          margin: 0.25rem;
        }

        .upcoming-date {
          background: #d1ecf1;
          color: #0c5460;
        }

        .past-date {
          background: #f8d7da;
          color: #721c24;
        }
      `}</style>

      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={"/facility/"} className="text-decoration-none">
                      <i className="pi pi-home me-1"></i>
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Service Vendor Management
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="container-fluid">
        <StatisticsCards />

        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body p-0">
                <TabView
                  activeIndex={activeTabIndex}
                  onTabChange={(e) => setActiveTabIndex(e.index)}
                  className="custom-tabview"
                >
                  {/* Vendors Tab */}
                  <TabPanel
                    header={`Service Vendors ${vendorStats.totalVendors > 0 ? `(${vendorStats.totalVendors})` : ""}`}
                    leftIcon="pi pi-users me-2"
                  >
                    <div className="p-3">
                      <DataTable
                        value={filteredVendors}
                        emptyMessage="No vendors found."
                        sortMode="multiple"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        stripedRows
                        loading={loading}
                        header={renderHeader}
                        className="p-datatable-striped"
                        responsiveLayout="scroll"
                        breakpoint="960px"
                      >
                        <Column
                          field="name"
                          header="Vendor Name"
                          sortable
                          className="font-semibold"
                        />
                        <Column
                          field="service"
                          header="Service Type"
                          sortable
                        />
                        <Column
                          field="contact"
                          header="Contact Information"
                          body={contactTemplate}
                        />
                        <Column
                          header="Assigned Assets"
                          body={assignedAssetsTemplate}
                        />
                        <Column
                          body={vendorActionTemplate}
                          header="Actions"
                          style={{ minWidth: "200px" }}
                        />
                      </DataTable>
                    </div>
                  </TabPanel>

                  {/* Asset Assignment Tab */}
                  <TabPanel
                    header="Asset Assignments"
                    leftIcon="pi pi-sitemap me-2"
                  >
                    <div className="p-3">
                      <DataTable
                        value={filteredVendors}
                        emptyMessage="No vendors available for asset assignment."
                        sortMode="multiple"
                        paginator
                        rows={10}
                        stripedRows
                        loading={loading}
                        className="p-datatable-striped"
                        responsiveLayout="scroll"
                      >
                        <Column
                          field="name"
                          header="Vendor Name"
                          sortable
                          className="font-semibold"
                        />
                        <Column
                          field="service"
                          header="Service Type"
                          sortable
                        />
                        <Column
                          header="Assigned Assets"
                          body={(rowData) => (
                            <div>
                              {rowData.assignedAssets &&
                              rowData.assignedAssets.length > 0 ? (
                                <div className="d-flex flex-wrap gap-1">
                                  {rowData.assignedAssets
                                    .slice(0, 3)
                                    .map((asset, index) => (
                                      <Chip
                                        key={index}
                                        label={asset.label || asset.name}
                                        className="p-chip-sm"
                                      />
                                    ))}
                                  {rowData.assignedAssets.length > 3 && (
                                    <span className="text-muted small">
                                      +{rowData.assignedAssets.length - 3} more
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted">
                                  No assets assigned
                                </span>
                              )}
                            </div>
                          )}
                        />
                        <Column
                          header="Agreement Status"
                          body={(rowData) => (
                            <Tag
                              value={rowData.agreement ? "Active" : "Pending"}
                              severity={
                                rowData.agreement ? "success" : "warning"
                              }
                              className="font-semibold"
                            />
                          )}
                        />
                        <Column
                          body={(rowData) => (
                            <Button
                              icon="pi pi-cog"
                              label="Assign Assets"
                              className="p-button-success p-button-sm"
                              onClick={() => handleAssetModalOpen(rowData)}
                            />
                          )}
                          header="Actions"
                        />
                      </DataTable>
                    </div>
                  </TabPanel>

                  {/* Maintenance Schedule Tab */}
                  <TabPanel
                    header={`Maintenance Schedule ${vendorStats.upcomingMaintenance > 0 ? `(${vendorStats.upcomingMaintenance})` : ""}`}
                    leftIcon="pi pi-calendar me-2"
                  >
                    <div className="p-3">
                      {maintenanceVendors.length > 0 ? (
                        <DataTable
                          value={maintenanceVendors}
                          emptyMessage="No scheduled maintenance found."
                          sortMode="multiple"
                          paginator
                          rows={10}
                          stripedRows
                          loading={loading}
                          className="p-datatable-striped"
                          responsiveLayout="scroll"
                        >
                          <Column
                            field="name"
                            header="Vendor Name"
                            sortable
                            className="font-semibold"
                          />
                          <Column
                            field="service"
                            header="Service Type"
                            sortable
                          />
                          <Column
                            header="Next Maintenance"
                            body={maintenanceDateTemplate}
                            sortable
                          />
                          <Column
                            header="Assigned Assets"
                            body={assignedAssetsTemplate}
                          />
                          <Column
                            body={maintenanceActionTemplate}
                            header="Actions"
                          />
                        </DataTable>
                      ) : (
                        <div className="text-center py-5">
                          <h4 className="mt-3 text-muted">
                            No Scheduled Maintenance
                          </h4>
                          <p className="text-muted">
                            No vendors have scheduled maintenance dates.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabPanel>

                  {/* Statistics Tab */}
                  <TabPanel header="Statistics" leftIcon="pi pi-chart-bar me-2">
                    <div className="p-4">
                      <div className="row">
                        {/* Vendor Overview */}
                        <div className="col-md-6 col-lg-4 mb-4">
                          <Card className="h-100 statistics-card">
                            <h5 className="card-title d-flex align-items-center mb-3">
                              <i className="pi pi-users text-primary me-2"></i>
                              Vendor Overview
                            </h5>
                            <div className="row text-center">
                              <div className="col-6">
                                <div className="border-end">
                                  <h3 className="text-primary mb-1">
                                    {vendorStats.totalVendors}
                                  </h3>
                                  <small className="text-muted">
                                    Total Vendors
                                  </small>
                                </div>
                              </div>
                              <div className="col-6">
                                <h3 className="text-success mb-1">
                                  {vendorStats.activeVendors}
                                </h3>
                                <small className="text-muted">Active</small>
                              </div>
                            </div>
                          </Card>
                        </div>

                        {/* Asset Coverage */}
                        <div className="col-md-6 col-lg-4 mb-4">
                          <Card className="h-100 statistics-card">
                            <h5 className="card-title d-flex align-items-center mb-3">
                              <i className="pi pi-sitemap text-success me-2"></i>
                              Asset Coverage
                            </h5>
                            <div className="row text-center">
                              <div className="col-6">
                                <div className="border-end">
                                  <h4 className="text-info mb-1">
                                    {vendorStats.assignedAssets}
                                  </h4>
                                  <small className="text-muted">Assigned</small>
                                </div>
                              </div>
                              <div className="col-6">
                                <h4 className="text-muted mb-1">
                                  {vendorStats.totalAssets -
                                    vendorStats.assignedAssets}
                                </h4>
                                <small className="text-muted">Unassigned</small>
                              </div>
                            </div>
                            <div className="mt-3">
                              <ProgressBar
                                value={
                                  vendorStats.totalAssets > 0
                                    ? (vendorStats.assignedAssets /
                                        vendorStats.totalAssets) *
                                      100
                                    : 0
                                }
                                className="mb-2"
                                style={{ height: "8px" }}
                              />
                              <small className="text-muted">
                                {vendorStats.totalAssets > 0
                                  ? `${Math.round((vendorStats.assignedAssets / vendorStats.totalAssets) * 100)}% Assets Covered`
                                  : "No assets"}
                              </small>
                            </div>
                          </Card>
                        </div>

                        {/* Quick Actions */}
                        <div className="col-md-6 col-lg-4 mb-4">
                          <Card className="h-100 statistics-card">
                            <h5 className="card-title d-flex align-items-center mb-3">
                              <i className="pi pi-cog text-dark me-2"></i>
                              Quick Actions
                            </h5>
                            <div className="d-grid gap-2">
                              <Button
                                label="Add New Vendor"
                                icon="pi pi-plus"
                                onClick={() => handleVendorModalOpen()}
                                className="p-button-primary"
                              />
                              <Button
                                label="Export Vendor List"
                                icon="pi pi-download"
                                className="p-button-outlined p-button-secondary"
                                onClick={() =>
                                  showToast(
                                    "info",
                                    "Export",
                                    "Export functionality coming soon",
                                  )
                                }
                              />
                              <Button
                                label="Maintenance Report"
                                icon="pi pi-calendar"
                                className="p-button-outlined p-button-secondary"
                                onClick={() =>
                                  showToast(
                                    "info",
                                    "Report",
                                    "Maintenance report functionality coming soon",
                                  )
                                }
                              />
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                </TabView>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vendor Add/Edit Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i
              className={`pi ${selectedVendor ? "pi-pencil" : "pi-plus"} me-2`}
            ></i>
            {selectedVendor ? "Edit Service Vendor" : "Add New Service Vendor"}
          </div>
        }
        visible={showVendorModal}
        onHide={() => setShowVendorModal(false)}
        style={{ width: "600px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        className="p-fluid"
      >
        <form onSubmit={handleVendorSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-user me-1"></i>
                  Vendor Name
                </label>
                <InputText
                  name="name"
                  value={vendorFormData.name}
                  onChange={handleVendorInputChange}
                  required
                  placeholder="Enter vendor name"
                />
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-briefcase me-1"></i>
                  Service Type
                </label>
                <InputText
                  name="service"
                  value={vendorFormData.service}
                  onChange={handleVendorInputChange}
                  required
                  placeholder="e.g., HVAC Maintenance, Security"
                />
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-phone me-1"></i>
                  Phone Number
                </label>
                <InputText
                  name="phone"
                  value={vendorFormData.phone}
                  onChange={handleVendorInputChange}
                  required
                  placeholder="+254XXXXXXXXX or 07XXXXXXXX"
                />
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-envelope me-1"></i>
                  Email Address
                </label>
                <InputText
                  name="email"
                  type="email"
                  value={vendorFormData.email}
                  onChange={handleVendorInputChange}
                  required
                  placeholder="vendor@example.com"
                />
              </div>
            </div>
          </div>

          <Divider />

          <div className="d-flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowVendorModal(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              type="submit"
              label={selectedVendor ? "Update Vendor" : "Add Vendor"}
              icon={selectedVendor ? "pi pi-check" : "pi pi-plus"}
              loading={loading}
              className="p-button-primary"
            />
          </div>
        </form>
      </Dialog>

      {/* Asset Assignment Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i className="pi pi-sitemap me-2"></i>
            Assign Assets & Services -{" "}
            <span className="text-primary">{selectedVendor?.name}</span>
          </div>
        }
        visible={showAssetModal}
        onHide={() => setShowAssetModal(false)}
        style={{ width: "700px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        className="p-fluid"
      >
        <div className="vendor-info-alert">
          <div className="d-flex align-items-center">
            <i className="pi pi-info-circle me-2"></i>
            <div>
              <small>
                Configuring services for:{" "}
                <strong>{selectedVendor?.name}</strong>
              </small>
              <br />
              <small className="text-muted">
                Service Type: {selectedVendor?.service}
              </small>
            </div>
          </div>
        </div>

        <form onSubmit={handleAssetSubmit}>
          <div className="row">
            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-file-text me-1"></i>
                  Service Agreement
                </label>
                <InputTextarea
                  value={assetFormData.agreement}
                  onChange={(e) =>
                    setAssetFormData({
                      ...assetFormData,
                      agreement: e.target.value,
                    })
                  }
                  placeholder="Enter service agreement details"
                  rows={4}
                  required
                />
              </div>
            </div>

            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-calendar me-1"></i>
                  Service Dates
                </label>
                <Calendar
                  value={assetFormData.dates}
                  onChange={(e) =>
                    setAssetFormData({ ...assetFormData, dates: e.value || [] })
                  }
                  selectionMode="multiple"
                  placeholder="Select service dates"
                  dateFormat="dd/mm/yy"
                  className="w-100"
                />
                <small className="text-muted">
                  Select multiple dates for scheduled maintenance
                </small>
              </div>
            </div>

            <div className="col-12 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-home me-1"></i>
                  Assigned Assets ({assetFormData.assignedAssets.length}{" "}
                  selected)
                </label>
                <MultiSelect
                  value={assetFormData.assignedAssets}
                  onChange={(e) =>
                    setAssetFormData({
                      ...assetFormData,
                      assignedAssets: e.value,
                    })
                  }
                  options={assets}
                  placeholder="Select assets to assign"
                  maxSelectedLabels={3}
                  className="w-100"
                  filter
                  emptyFilterMessage="No assets found"
                />
                <small className="text-muted">
                  Select assets that this vendor will service
                </small>
              </div>
            </div>
          </div>

          <Divider />

          <div className="d-flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowAssetModal(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              type="submit"
              label="Update Assignment"
              icon="pi pi-check"
              loading={loading}
              className="p-button-success"
            />
          </div>
        </form>
      </Dialog>

      {/* Maintenance Details Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i className="pi pi-calendar me-2"></i>
            Maintenance Details -{" "}
            <span className="text-primary">{selectedVendor?.name}</span>
          </div>
        }
        visible={showMaintenanceModal}
        onHide={() => setShowMaintenanceModal(false)}
        style={{ width: "700px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
      >
        <div className="maintenance-details">
          <div className="row">
            <div className="col-md-6 mb-3">
              <h6 className="d-flex align-items-center">
                <i className="pi pi-user me-2 text-primary"></i>
                Vendor Information
              </h6>
              <div className="ms-4">
                <div className="mb-2">
                  <strong>Name:</strong> {selectedVendor?.name}
                </div>
                <div className="mb-2">
                  <strong>Service:</strong> {selectedVendor?.service}
                </div>
                <div className="mb-2">
                  <strong>Phone:</strong> {selectedVendor?.phone}
                </div>
                <div className="mb-2">
                  <strong>Email:</strong> {selectedVendor?.email}
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <h6 className="d-flex align-items-center">
                <i className="pi pi-home me-2 text-success"></i>
                Assigned Assets ({selectedVendor?.assignedAssets?.length || 0})
              </h6>
              <div className="ms-4">
                {selectedVendor?.assignedAssets &&
                selectedVendor.assignedAssets.length > 0 ? (
                  <div className="d-flex flex-wrap gap-1">
                    {selectedVendor.assignedAssets.map((asset, index) => (
                      <Chip
                        key={index}
                        label={asset.label || asset.name}
                        className="p-chip-sm"
                        icon="pi pi-home"
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No assets assigned</p>
                )}
              </div>
            </div>

            <div className="col-12 mb-3">
              <h6 className="d-flex align-items-center">
                <i className="pi pi-file-text me-2 text-info"></i>
                Service Agreement
              </h6>
              <div className="ms-4">
                <div className="border rounded p-3 bg-light">
                  {selectedVendor?.agreement || "No agreement details provided"}
                </div>
              </div>
            </div>

            <div className="col-12">
              <h6 className="d-flex align-items-center">
                <i className="pi pi-calendar me-2 text-warning"></i>
                Maintenance Schedule
              </h6>
              <div className="ms-4">
                {selectedVendor?.dates && selectedVendor.dates.length > 0 ? (
                  <div>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <h7 className="text-success">Upcoming Dates</h7>
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {selectedVendor.dates
                            .filter((date) => new Date(date) >= new Date())
                            .sort((a, b) => new Date(a) - new Date(b))
                            .slice(0, 5)
                            .map((date, index) => (
                              <Chip
                                key={index}
                                label={new Date(date).toLocaleDateString()}
                                className="upcoming-date date-chip"
                                icon="pi pi-calendar-plus"
                              />
                            ))}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <h7 className="text-muted">Past Dates</h7>
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {selectedVendor.dates
                            .filter((date) => new Date(date) < new Date())
                            .sort((a, b) => new Date(b) - new Date(a))
                            .slice(0, 3)
                            .map((date, index) => (
                              <Chip
                                key={index}
                                label={new Date(date).toLocaleDateString()}
                                className="past-date date-chip"
                                icon="pi pi-calendar-minus"
                              />
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <i
                      className="pi pi-calendar text-muted"
                      style={{ fontSize: "2rem" }}
                    ></i>
                    <p className="text-muted mt-2">
                      No maintenance dates scheduled
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Divider />

        <div className="d-flex justify-content-end pt-3">
          <Button
            type="button"
            label="Close"
            icon="pi pi-times"
            onClick={() => setShowMaintenanceModal(false)}
            className="p-button-outlined p-button-secondary"
          />
        </div>
      </Dialog>
    </Layout>
  );
};

export default ServiceVendorManagement;
